import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Star, RefreshCw, Download, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  ["all", "All"],
  ["responded", "Answered"],
  ["awaiting", "No answer yet"],
  ["converted", "Converted"],
];

const Stars = ({ value }) => {
  const n = Number(value) || 0;
  if (!n) return <span className="text-slate-600 text-xs">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${n} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />
      ))}
    </span>
  );
};

export default function AdminTrialSurveys() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/trials/survey-results`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load survey results");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const needle = q.trim().toLowerCase();
    return all.filter((r) => {
      if (filter === "responded" && !r.responded_at) return false;
      if (filter === "awaiting" && r.responded_at) return false;
      if (filter === "converted" && !r.converted) return false;
      if (!needle) return true;
      return [r.child_name, r.parent_name, r.coach_name, r.comments]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [data, filter, q]);

  const s = data?.summary || {};

  const exportCsv = () => {
    const head = ["Child", "Parent", "Trial date", "Coach", "Overall", "Likely to enrol",
      "Welcomed", "Comments", "Answered on", "Outcome"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      r.child_name, r.parent_name, r.trial_date, r.coach_name || "", r.overall ?? "",
      r.likely ?? "", r.welcome || "", r.comments || "",
      (r.responded_at || "").slice(0, 10), r.converted ? "Converted" : r.pipeline_stage || "",
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `trial-surveys-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6" data-testid="trial-surveys-page">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Trial survey results</h1>
            <p className="text-sm text-slate-400 mt-1">
              What parents said after their child&apos;s trial, and whether they went on to register.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
              onClick={load} disabled={loading} data-testid="surveys-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
            </Button>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
              onClick={exportCsv} disabled={!rows.length} data-testid="surveys-export">
              <Download className="w-3.5 h-3.5 mr-1" />CSV
            </Button>
            <Link to="/admin/trials" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm"
              data-testid="surveys-back">
              <ArrowLeft className="w-4 h-4" />Trials
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="surveys-summary">
          {[
            ["Sent", s.sent ?? 0, "surveys-stat-sent"],
            ["Answered", `${s.responded ?? 0} (${s.response_rate ?? 0}%)`, "surveys-stat-responded"],
            ["Avg experience", s.avg_overall ?? "—", "surveys-stat-overall"],
            ["Avg likely to enrol", s.avg_likely ?? "—", "surveys-stat-likely"],
            ["Converted", s.converted ?? 0, "surveys-stat-converted"],
          ].map(([label, value, tid]) => (
            <Card key={label} className="p-3 bg-slate-900/60 border-slate-800" data-testid={tid}>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
              <p className="text-xl font-semibold text-white mt-1">{value}</p>
            </Card>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map(([key, label]) => (
            <Button key={key} size="sm"
              variant={filter === key ? "default" : "outline"}
              className={filter === key ? "bg-cyan-600 hover:bg-cyan-500 h-8 text-xs"
                : "border-slate-700 text-slate-300 h-8 text-xs"}
              onClick={() => setFilter(key)} data-testid={`surveys-filter-${key}`}>
              {label}
            </Button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search child, parent, coach or comment"
            className="bg-slate-900 border-slate-700 h-8 text-xs max-w-xs ml-auto"
            data-testid="surveys-search" />
        </div>

        {loading ? (
          <p className="text-sm text-slate-400" data-testid="surveys-loading">Loading…</p>
        ) : !rows.length ? (
          <Card className="p-8 bg-slate-900/50 border-slate-800 text-center" data-testid="surveys-empty">
            <MessageSquare className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              No surveys here yet. They go out the morning after a trial, to trialists the register
              marked present.
            </p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="surveys-list">
            {rows.map((r) => (
              <Card key={r.trial_id} className="p-4 bg-slate-900/50 border-slate-800"
                data-testid={`survey-row-${r.trial_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{r.child_name}</span>
                      {r.age_group && <Badge className="text-[10px] bg-slate-700 text-slate-200">{r.age_group}</Badge>}
                      {r.converted ? (
                        <Badge className="text-[10px] bg-green-600/30 text-green-300" data-testid={`survey-converted-${r.trial_id}`}>Converted</Badge>
                      ) : r.responded_at ? (
                        <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300">Answered</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-500/20 text-amber-300">No answer yet</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {r.parent_name} · trial {r.trial_date}
                      {r.coach_name ? ` · coach ${r.coach_name}` : ""}
                      {r.sent_at ? ` · survey sent ${String(r.sent_at).slice(0, 10)}${r.sent_channel ? ` by ${r.sent_channel}` : ""}` : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] text-slate-500">Experience</p>
                    <Stars value={r.overall} />
                    <p className="text-[11px] text-slate-500 mt-1">Likely to enrol</p>
                    <Stars value={r.likely} />
                  </div>
                </div>
                {(r.welcome || r.comments || (r.answers || []).some((a) => !["overall", "likely", "welcome", "comments"].includes(a.id))) && (
                  <div className="mt-2 pt-2 border-t border-slate-800 text-xs text-slate-300 space-y-1">
                    {r.welcome && <p data-testid={`survey-welcome-${r.trial_id}`}>Coach made them feel welcome: <span className="text-white">{r.welcome}</span></p>}
                    {(r.answers || [])
                      .filter((a) => !["overall", "likely", "welcome", "comments"].includes(a.id))
                      .map((a) => (
                        <p key={a.id} data-testid={`survey-extra-${r.trial_id}-${a.id}`}>
                          {a.label}: <span className="text-white">{String(a.value)}</span>
                        </p>
                      ))}
                    {r.comments && <p className="italic text-slate-200" data-testid={`survey-comment-${r.trial_id}`}>“{r.comments}”</p>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
